/**
 * Store de UI - Zustand
 *
 * Maneja el estado de la interfaz de usuario:
 * - Modales
 * - Toasts
 * - Preferencias visuales
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Toast, ModalState, UserPreferences } from '@/core/types';
import { DEFAULT_PREFERENCES } from '@/core/types';
import { generateId } from '@/core/utils';

type ActiveView = 'dashboard' | 'stats' | 'pack' | 'timer' | 'achievements';

interface UIState {
  // === Vista activa ===
  activeView: ActiveView;
  setActiveView: (view: ActiveView) => void;

  // === Toasts ===
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // === Modal ===
  modal: ModalState;
  openModal: (type: ModalState['type'], data?: unknown) => void;
  closeModal: () => void;

  // === Preferences ===
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  resetPreferences: () => void;

  // === Sidebar (mobile) ===
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;

  // === Loading ===
  isLoading: boolean;
  setLoading: (loading: boolean) => void;

  // === Modo Día Difícil ===
  hardDayMode: boolean;
  toggleHardDayMode: () => void;
  setHardDayMode: (enabled: boolean) => void;

  // === Modo Fin de Semana ===
  weekendMode: boolean;
  toggleWeekendMode: () => void;
  setWeekendMode: (enabled: boolean) => void;

  // === Modo Focus ===
  focusMode: boolean;
  focusProtocolId: string | null;
  toggleFocusMode: () => void;
  setFocusMode: (enabled: boolean, protocolId?: string | null) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // === Vista activa ===
      activeView: 'dashboard',

      setActiveView: (view) => {
        set({ activeView: view });
      },

      // === Toasts ===
      toasts: [],

      addToast: (toast) => {
        const id = generateId('toast');
        const newToast: Toast = {
          ...toast,
          id,
          duration: toast.duration ?? 5000,
        };

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));

        // Auto-remove after duration
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },

      // === Modal ===
      modal: {
        isOpen: false,
        type: null,
        data: undefined,
      },

      openModal: (type, data) => {
        set({
          modal: {
            isOpen: true,
            type,
            data,
          },
        });
      },

      closeModal: () => {
        set({
          modal: {
            isOpen: false,
            type: null,
            data: undefined,
          },
        });
      },

      // === Preferences ===
      preferences: DEFAULT_PREFERENCES,

      updatePreferences: (updates) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...updates,
          },
        }));
      },

      resetPreferences: () => {
        set({ preferences: DEFAULT_PREFERENCES });
      },

      // === Sidebar ===
      isSidebarOpen: false,

      toggleSidebar: () => {
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
      },

      closeSidebar: () => {
        set({ isSidebarOpen: false });
      },

      // === Loading ===
      isLoading: false,

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      // === Modo Día Difícil ===
      hardDayMode: false,

      toggleHardDayMode: () => {
        set((state) => ({ hardDayMode: !state.hardDayMode }));
      },

      setHardDayMode: (enabled) => {
        set({ hardDayMode: enabled });
      },

      // === Modo Fin de Semana ===
      weekendMode: false,

      toggleWeekendMode: () => {
        set((state) => ({ weekendMode: !state.weekendMode }));
      },

      setWeekendMode: (enabled) => {
        set({ weekendMode: enabled });
      },

      // === Modo Focus ===
      focusMode: false,
      focusProtocolId: null,

      toggleFocusMode: () => {
        set((state) => ({
          focusMode: !state.focusMode,
          focusProtocolId: state.focusMode ? null : state.focusProtocolId,
        }));
      },

      setFocusMode: (enabled, protocolId = null) => {
        set({
          focusMode: enabled,
          focusProtocolId: enabled ? protocolId : null,
        });
      },
    }),
    {
      name: 'exocortex-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        preferences: state.preferences,
      }),
    }
  )
);

// === Helpers ===

/**
 * Shortcut para mostrar toast de éxito
 */
export const showSuccess = (title: string, message?: string) => {
  useUIStore.getState().addToast({
    type: 'success',
    title,
    ...(message !== undefined && { message }),
  });
};

/**
 * Shortcut para mostrar toast de error
 */
export const showError = (title: string, message?: string) => {
  useUIStore.getState().addToast({
    type: 'error',
    title,
    duration: 8000, // Más tiempo para errores
    ...(message !== undefined && { message }),
  });
};

/**
 * Shortcut para mostrar toast informativo
 */
export const showInfo = (title: string, message?: string) => {
  useUIStore.getState().addToast({
    type: 'info',
    title,
    ...(message !== undefined && { message }),
  });
};

/**
 * Shortcut para mostrar toast de advertencia
 */
export const showWarning = (title: string, message?: string) => {
  useUIStore.getState().addToast({
    type: 'warning',
    title,
    ...(message !== undefined && { message }),
  });
};
